/**
 * Export one answer as a document the reader can keep.
 *
 * The CSV/Excel downloads take the result set; this takes the reply itself —
 * what the agent wrote — for pasting into a ticket, a review doc, or a mail
 * thread. Two things are deliberately left out:
 *
 *  - the result table: that is what the spreadsheet formats are for, and rows
 *    pasted into prose are unreadable anyway;
 *  - the query / API requests behind the answer: the export is the answer as
 *    something to hand to a reader, and the SQL is plumbing that reader never
 *    asked for (it also names tables and filters that have no business in a
 *    forwarded document).
 *
 * PDF is generated client-side and downloads directly, exactly like the
 * Markdown file — no print dialog, no popup. The composed Markdown is parsed
 * with remark (the same parser react-markdown uses for the live chat bubble)
 * into a real syntax tree, then walked into pdfmake's declarative content
 * model — pdfmake itself is dynamically imported so its weight is paid on
 * the first PDF click, not on page load.
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import rehypeParse from 'rehype-parse';
import config from '../config';
import interRegularUrl from '../fonts/Inter-Regular.ttf';
import interBoldUrl from '../fonts/Inter-Bold.ttf';
import interItalicUrl from '../fonts/Inter-Italic.ttf';
import { sanitizeHTML } from './sanitize';
import { splitMentions } from './mentions';
import { DARK_THEME_IDS, PDF_PALETTES, PDF_SERIES } from '../theme/tokens';

/** Text out of the several shapes an answer body arrives in. */
export const answerText = (content) => {
  if (typeof content === 'string') return content;
  if (content == null || content === false) return '';
  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.query || JSON.stringify(item);
        return String(item);
      })
      .join('\n\n');
  }
  if (typeof content === 'object') return content.query || JSON.stringify(content, null, 2);
  return String(content);
};

/**
 * Drop the ##N$$ citation markers the agent embeds for the chat UI's inline
 * citation chips. An exported document has no reference panel for them to
 * point at, so they go entirely — the answer reads as clean prose.
 */
export const replaceCitationMarkers = (text) => text
  .replace(/ *##\d+\$+/g, '')
  .replace(/[ \t]+$/gm, '');

/**
 * Is this answer body the result set rather than something written?
 *
 * The same `message` field carries either, depending on the turn: a rag reply
 * puts prose there, while a sql reply puts the rows there — as a markdown table
 * or as an array of row objects.
 */
export const isResultSet = (content) => {
  if (Array.isArray(content)) return true;
  if (content && typeof content === 'object') return !content.query;
  if (typeof content !== 'string') return false;
  const text = content.trim();
  if (text.startsWith('|')) return true;
  // A multi-step SQL turn puts the data-source dump here — "### Step 1 - ..."
  // headings wrapping HTML tables — which is the result set under a heading, not
  // something written. Recognising only the markdown-pipe and JSON shapes let
  // that dump through as prose, so every table landed in the PDF the CSV and
  // Excel downloads exist to carry.
  if (/<table[\s>]/i.test(text)) return true;
  return text.startsWith('[') || text.startsWith('{');
};

/**
 * Whether an answer follows the RCA fixed answer format
 * (docs/qad/harness/prompt_v12/rca_answer_format.md on the `discover` side): the
 * same seven fields `get_case` returns — Symptoms, Environment, Resolution, Root
 * Cause, Workaround, Defect, Knowledge Article — always in this order, never with
 * one silently dropped. There is no typed field on the turn marking it as one —
 * the shape is a prompt convention, not a server-reported type — so this is
 * detected by heading text instead.
 *
 * The model renders each heading as either a real Markdown heading ("## Root
 * Cause") or a bold lead-in on its own line ("**Root Cause**"), and sometimes
 * numbers them — either after the heading marker ("## 4. Workaround") or before
 * it ("1. **Symptoms**", the literal numbered-list form discover's own
 * prompt_v12/rca_answer_format.md instructs the model to use) — this matches
 * every combination and tolerates the numbering, then requires at least three
 * of the seven distinctive section names before calling it an RCA answer,
 * enough that an ordinary answer which merely has a "Workaround" or "Defect"
 * heading of its own does not trip it.
 */
const RCA_HEADING_RE =
  /^(?:\d+[.)][ \t]*)?(?:#{1,6}[ \t]*|\*\*[ \t]*)(?:\d+[.):][ \t]*)?(symptoms|environment|resolution|root cause|workaround|defect|knowledge article)\b/gim;

export const isRcaAnswer = (content) => {
  const text = answerText(content);
  if (!text) return false;
  return new Set([...text.matchAll(RCA_HEADING_RE)].map((m) => m[1].toLowerCase())).size >= 3;
};

/**
 * A timestamp a reader in another office can place: local time plus the zone
 * it was local to. Without the zone an exported document says 3:04 PM and
 * means nothing to whoever it was forwarded to.
 */
export const stampText = (date) => date.toLocaleString(undefined, { timeZoneName: 'short' });

/**
 * A display name is not Markdown, and it arrives from the IdP rather than from
 * us — `Options`/`MainContent` pass the OIDC profile name straight through. A
 * newline in it ends the italic stamp and turns the rest of the name into
 * blocks of the document; `*`, `_`, `[` and friends open emphasis or a link.
 * Both reach remark on the PDF path, which is why this sits at the Markdown
 * composition boundary and not inside `exportedLine` — the HTML export feeds
 * the same stamp through its own `escapeHtml`, where a backslash would show
 * up as a backslash.
 */
const markdownText = (value) => (value
  ? String(value).replace(/\s+/g, ' ').replace(/[\\`*_[\]<>#|~]/g, '\\$&')
  : value);

/**
 * The "who and when" stamp every export carries. One definition so the answer
 * exports and the session exports cannot drift into stamping differently.
 * `exportedBy` is emitted verbatim: each caller escapes for its own format.
 */
export const exportedLine = (exportedBy, at = new Date()) => {
  const who = exportedBy && String(exportedBy).trim() ? ` by ${String(exportedBy).trim()}` : '';
  return `Exported${who} · ${stampText(at)}`;
};

/**
 * Compose the Markdown document: the question, when it was asked, who exported
 * it and when. Nothing else — see the header comment for why the query and
 * requests stay out.
 *
 * @param {{question?: string, answer?: *, description?: string,
 *          askedAt?: Date, exportedBy?: string}} turn
 */
export const buildMarkdown = ({
  question,
  answer,
  description,
  askedAt,
  exportedBy,
} = {}) => {
  const parts = [];
  const written = replaceCitationMarkers(answerText(description)).trim();
  const body = replaceCitationMarkers(answerText(answer)).trim();

  parts.push(question ? `# ${markdownMentions(question.trim())}` : '# Answer');
  if (askedAt instanceof Date && !Number.isNaN(askedAt.getTime())) {
    parts.push(`_Asked ${stampText(askedAt)}_`);
  }
  parts.push(`_${exportedLine(markdownText(exportedBy))}_`);

  const bodyIsProse = !!body && !isResultSet(answer);

  if (written) parts.push(written);
  // Add the body only when it is prose the reader would want, and not a repeat
  // of what is already written above.
  if (bodyIsProse && body !== written) parts.push(body);
  if (!written && !bodyIsProse) parts.push('_This answer had no text._');

  return `${parts.join('\n\n')}\n`;
};

/**
 * Shared convention for the message list: author_id 0 (or the string "0") is
 * the agent's own turn; anything else — including a missing id on malformed
 * data — is the user's. Kept in one place so the export composer and the
 * live message list (MainContent.jsx) can't drift into disagreeing about it.
 */
export const isUserMessage = (authorId) => authorId !== 0 && authorId !== '0' && authorId !== null;

/**
 * Walk a session into segments tagged by what they are: the title, a real
 * conversation-turn question, or a stretch of written/body prose. Shared by
 * `buildSessionMarkdown` (which flattens this to one string for the .md
 * download, where "## question" is a perfectly good heading) and the PDF
 * path (which cannot afford that flattening — an agent's own answer often
 * contains its own "## " subheadings, e.g. structuring a long answer into
 * sections, and those must never be mistaken for a real asked question just
 * because they share a heading depth with one).
 *
 * @param {string} title    session summary, used as the document title
 * @param {Array}  messages the screen's own formatted message list — each
 *                          { author_id, message, description }; author_id 0
 *                          is the agent, anything else is the user.
 */
const sessionSegments = (title, messages = [], { exportedBy } = {}) => {
  const segments = [
    { type: 'title', text: (title || '').trim() || 'Discover chat' },
    { type: 'prose', text: `_${exportedLine(markdownText(exportedBy))}_` },
  ];

  // Mirrors ChatScreen.jsx's own visibleMessages filter: a bot message the
  // app synthesises at index 0 (the default "Hi! I'm your assistant..."
  // greeting) never appears in the live transcript, so the export shouldn't
  // show it either.
  const visibleMessages = messages[0]?.author_id === 0 && !messages[0]?.isError
    ? messages.slice(1)
    : messages;

  let pendingQuestion = null;
  let turns = 0;

  // A question that never got its own answer turn (e.g. a failed send that
  // was retried) still belongs in the transcript rather than vanishing under
  // the next question that happened to follow it.
  const flushUnanswered = () => {
    if (!pendingQuestion) return;
    segments.push({ type: 'question', text: pendingQuestion });
    segments.push({ type: 'prose', text: '_No answer yet._' });
    turns += 1;
    pendingQuestion = null;
  };

  visibleMessages.forEach((m) => {
    if (isUserMessage(m.author_id)) {
      const q = replaceCitationMarkers(answerText(m.message)).trim();
      if (q) {
        flushUnanswered();
        pendingQuestion = q;
      }
      return;
    }
    const written = replaceCitationMarkers(answerText(m.description)).trim();
    const body = replaceCitationMarkers(answerText(m.message)).trim();
    const bodyIsProse = !!body && !isResultSet(m.message);

    const answerParts = [];
    if (written) answerParts.push(written);
    if (bodyIsProse && body !== written) answerParts.push(body);
    if (!answerParts.length) {
      // The turn answered with a result table — the transcript says so rather
      // than silently dropping the question.
      answerParts.push('_This answer was a result table — use the data downloads on the message itself._');
    }

    if (pendingQuestion) {
      segments.push({ type: 'question', text: pendingQuestion });
      pendingQuestion = null;
      turns += 1;
    }
    answerParts.forEach((text) => segments.push({ type: 'prose', text }));
  });

  // A trailing question the agent never answered (e.g. still streaming when
  // exported) still belongs in the transcript.
  flushUnanswered();

  if (!turns) segments.push({ type: 'prose', text: '_This chat had no messages._' });
  return segments;
};

/**
 * Compose the whole session as one Markdown document, mirroring the chat's
 * own shape: the session title on top, then each question as a heading with
 * the written answer under it, in conversation order. Result tables and the
 * queries stay out for the same reasons as the single-answer export; a
 * table-only turn keeps its question with a note so the transcript still
 * reads complete.
 */
export const buildSessionMarkdown = (title, messages = [], { exportedBy } = {}) => {
  const parts = sessionSegments(title, messages, { exportedBy }).map((seg) => {
    if (seg.type === 'title') return `# ${seg.text}`;
    // Only the flattened Markdown gets the code spans. `sessionSegments` stays
    // clean so the PDF path, which parses the same segments, paints its own
    // chip rather than rendering the mention in Courier as well.
    if (seg.type === 'question') return `## ${markdownMentions(seg.text)}`;
    return seg.text;
  });
  return `${parts.join('\n\n')}\n`;
};

/**
 * Whether a turn has anything worth exporting as a document.
 *
 * Shares `isResultSet` with the composer so the menu never offers a file that
 * would come out saying the answer had no text, and strips citation markers
 * first so a description that is purely a `##N$$` marker — which the composer
 * reduces to nothing — doesn't pass this check either.
 */
export const hasExportableText = (turn) => {
  if (!turn) return false;
  if (typeof turn.description === 'string' && replaceCitationMarkers(turn.description).trim()) return true;
  if (isResultSet(turn.answer)) return false;
  return !!replaceCitationMarkers(answerText(turn.answer)).trim();
};

/** A filesystem-safe stem taken from the question. */
export const exportFilename = (question, extension) => {
  const stem = (question || 'discover-answer')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'discover-answer';
  return `${stem}.${extension}`;
};

// Page geometry. Page 1 is a cover with even margins; every later page carries
// a rail down its left edge (brand, title, page number), so the body's left
// margin clears the rail plus a gutter.
const A4_WIDTH = 595.28;
const RAIL_WIDTH = 110;
const RAIL_PAD = 20;
const COVER_MARGINS = [56, 56, 56, 56];
const BODY_MARGINS = [RAIL_WIDTH + 34, 44, 44, 60];
// The body's text width — every full-width canvas line/card uses this so they
// line up with each other and with the page edges.
const CONTENT_WIDTH = Math.floor(A4_WIDTH - BODY_MARGINS[0] - BODY_MARGINS[2]);

/** `a` laid over `b` at opacity `t`, both #rrggbb — pdfmake paints no alpha. */
const mixHex = (a, b, t) => `#${[1, 3, 5].map((i) => Math.round(
  parseInt(a.slice(i, i + 2), 16) * t + parseInt(b.slice(i, i + 2), 16) * (1 - t),
).toString(16).padStart(2, '0')).join('')}`;

/**
 * The document prints in the chat theme the reader has active, so a PDF made
 * in Ocean looks like Ocean. `mention` is the @mention chip on a question
 * bubble: 40% of the accent over the bubble's own fill — pdfmake cannot draw
 * the inset ring that carries the chip's edge on screen, so the fill does that
 * work alone.
 */
const paletteFor = (theme) => {
  const p = PDF_PALETTES[theme] || PDF_PALETTES.light;
  const series = PDF_SERIES[DARK_THEME_IDS.includes(theme) ? 'dark' : 'light'];
  return { ...p, series, mention: mixHex(p.accent, p.accentSoft, 0.4) };
};

// Set once per document build, like activeBodyFont below: every helper reads
// the palette of the document being built without threading it through each
// signature.
let pal = paletteFor('light');

/** The theme the app has active right now (ThemeProvider sets it on <html>). */
const activeTheme = () => (typeof document !== 'undefined'
  ? document.documentElement.getAttribute('data-theme') || undefined
  : undefined);

/**
 * Inter has no emoji glyphs, so an agent's "📊 Overall Standing" printed as a
 * blank box before the words. Pictographs go, with one following space, along
 * with the joiners and variation selectors that build them. ©, ® and ™ are
 * Extended_Pictographic too, but Inter draws them, so they stay.
 */
const EMOJI_RE = /(?:(?![©®™])\p{Extended_Pictographic}|[\u{1f1e6}-\u{1f1ff}\u{1f3fb}-\u{1f3ff}️‍⃣])+[ \t]?/gu;
const stripEmoji = (text) => String(text).replace(EMOJI_RE, '');

/** Depth-first plain string out of an inline mdast/hast subtree — no styling. */
const plainText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return stripEmoji(node.value || '');
  // A code span holds its text in `value` and has no children, so walking only
  // children silently dropped it — and the question headings this reads carry
  // their mentions as code spans.
  if (node.type === 'inlineCode') return node.value || '';
  if (node.children) return node.children.map(plainText).join('');
  return '';
};

/**
 * A file path, a hyphenated word like "dataset-handle", or a CamelCase
 * identifier has no spaces at all, so pdfmake's word-based wrapping treats
 * it as one unbreakable token — its full width becomes the table column's
 * minimum width, and inside a narrow column (several long identifier-heavy
 * columns competing for the same 483pt) that width can exceed what's left,
 * with the overflow silently clipped rather than wrapped. Zero-width spaces
 * after separators, and at CamelCase boundaries, give the wrapper places to
 * break; invisible, so they cost nothing when a break isn't needed.
 *
 * Only applied within already-whitespace-delimited tokens of some length —
 * short ones (a decimal like "170.0", a spaced-out "-") never risk overflow
 * on their own, and touching them would plant an invisible character in
 * ordinary prose for no reason. Applied to every long run, not just code
 * spans — plain prose ("dataset-handle" written without backticks) hits the
 * exact same wall. Every inline run passes through here, so this is also
 * where emoji are dropped (see stripEmoji).
 */
const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);
const breakable = (text) => stripEmoji(text).replace(/\S{12,}/g, (word) => word
  .replace(/([/._-])/g, `$1${ZERO_WIDTH_SPACE}`)
  .replace(/([a-z0-9])([A-Z])/g, `$1${ZERO_WIDTH_SPACE}$2`));

// A link whose whole text is an issue key ("AIH-391", "PROJ-12").
const ISSUE_KEY_RE = /^[A-Z][A-Z0-9]{1,9}-\d+$/;

/**
 * A link's runs: underlined in the accent, except an issue key, which prints
 * as a tinted chip — the same mark the chat gives a ticket — so a list of
 * ticket highlights scans by key.
 */
const linkRuns = (runs, url) => {
  const isKey = ISSUE_KEY_RE.test(runs.map((r) => r.text).join('').replace(ZERO_WIDTH_RE, ''));
  return runs.map((r) => (isKey
    ? { ...r, link: url, color: pal.accentInk, bold: true, background: pal.accentSoft }
    : { ...r, link: url, color: pal.accentInk, decoration: 'underline' }));
};

/** mdast inline nodes -> a flat array of pdfmake text runs (bold/italic/link kept per-run). */
const mdastInline = (nodes = []) => nodes.flatMap((node) => {
  switch (node.type) {
    case 'text':
      return node.value ? [{ text: breakable(node.value) }] : [];
    case 'strong':
      return mdastInline(node.children).map((r) => ({ ...r, bold: true }));
    case 'emphasis':
      return mdastInline(node.children).map((r) => ({ ...r, italics: true }));
    case 'delete':
      return mdastInline(node.children).map((r) => ({ ...r, decoration: 'lineThrough' }));
    case 'inlineCode':
      return [{ text: breakable(node.value), font: 'Courier' }];
    case 'link':
      return linkRuns(mdastInline(node.children), node.url);
    case 'break':
      return [{ text: '\n' }];
    default:
      return node.children ? mdastInline(node.children) : [];
  }
});

/**
 * The same runs, with each @mention carrying the chip's background.
 *
 * `breakable()` has already planted zero-width spaces inside any long token,
 * which would split a long mention in the middle of the match, so each run is
 * stripped back to its plain text, divided, and re-broken per piece — the
 * wrap hints land in exactly the same places, since a mention and its
 * neighbour are separated by a space and never share a token.
 */
const ZERO_WIDTH_RE = new RegExp(ZERO_WIDTH_SPACE, 'g');
const withMentionChips = (runs, background = pal.mention) => runs.flatMap((run) => splitMentions(String(run.text ?? '').replace(ZERO_WIDTH_RE, ''))
  .map((part) => ({ ...run, text: breakable(part.text), ...(part.isMention ? { background } : {}) })));

/**
 * Mentions as code spans — the only chip Markdown has. It does change the
 * literal characters someone pastes out of the file, which is the cost of
 * marking a mention in a format that has no styling of its own.
 */
const markdownMentions = (text) => splitMentions(String(text ?? ''))
  .map((part) => (part.isMention ? `\`${part.text}\`` : part.text))
  .join('');

/** A short standalone string (e.g. a literal question) -> its inline runs, via a mini parse. */
const parseInlineOnly = (text) => {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(text);
  const runs = (tree.children || []).flatMap((n) => (n.type === 'paragraph' ? mdastInline(n.children) : []));
  return runs.length ? runs : [{ text }];
};

/** hast (parsed HTML) inline nodes -> the same flat pdfmake run shape as mdastInline. */
const hastInline = (nodes = []) => nodes.flatMap((node) => {
  if (node.type === 'text') return node.value ? [{ text: breakable(node.value) }] : [];
  if (node.type !== 'element') return [];
  const kids = hastInline(node.children);
  switch (node.tagName) {
    case 'strong': case 'b':
      return kids.map((r) => ({ ...r, bold: true }));
    case 'em': case 'i':
      return kids.map((r) => ({ ...r, italics: true }));
    case 'code':
      return kids.map((r) => ({ ...r, font: 'Courier' }));
    case 'br':
      return [{ text: '\n' }];
    case 'a':
      return linkRuns(kids, node.properties?.href);
    default:
      return kids;
  }
});

/** A borderless single-cell table used as a tinted panel (bubble/code background). */
const panel = (content, { fillColor, margin = [0, 0, 0, 0], contentMargin = [0, 0, 0, 0], unbreakable = false }) => ({
  table: { widths: ['*'], body: [[{ ...content, margin: contentMargin }]] },
  layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => fillColor },
  margin,
  ...(unbreakable ? { unbreakable: true } : {}),
});

// The badge column's width matches the circle's own diameter exactly — it
// used to be wider than the circle (26 vs 18), so the "Q"'s `alignment:
// center` centered against the wider column instead of the circle, landing
// visibly right of true center. columnGap supplies the gap that extra width
// used to give for free.
const BADGE_SIZE = 18;
const BUBBLE_GAP = 10;
const BUBBLE_PAD_X = 14;
const BUBBLE_PAD_Y = 10;
const BUBBLE_RADIUS = 10;
const BUBBLE_FONT_SIZE = 12;

// Set once per document build — the one place in this file that needs to
// know the body font ahead of layout, to measure how many lines a question
// will wrap to before it's drawn. See wrappedLineCount below for why.
let activeBodyFont = 'Helvetica';
const FONT_FAMILY_CSS = { Inter: 'Inter, sans-serif', Helvetica: 'Helvetica, Arial, sans-serif' };

/**
 * A rounded card (the question bubble's fill) needs its height before it's
 * drawn — unlike the auto-sizing table it replaces, a pdfmake canvas rect
 * can't size itself to its own text. Estimates the wrapped line count using
 * the browser's own text metrics for the same font pdfmake embeds, so the
 * estimate tracks the real font rather than a blind guess. Falls back to an
 * average-character-width estimate where no canvas 2D context is available
 * (e.g. this test environment) — errs toward overestimating on either path,
 * so the card comes out a touch tall rather than the text overflowing it.
 */
const wrappedLineCount = (runs, fontSize, maxWidth) => {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const ctx = canvas ? canvas.getContext('2d') : null;
  const family = FONT_FAMILY_CSS[activeBodyFont] || FONT_FAMILY_CSS.Helvetica;
  const measure = ctx
    ? (word, bold, italics) => {
      ctx.font = `${italics ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSize}px ${family}`;
      return ctx.measureText(word).width;
    }
    : (word) => word.length * fontSize * 0.62;

  let lines = 1;
  let x = 0;
  runs.forEach((run) => {
    String(run.text).split(/(\s+)/).forEach((word) => {
      if (!word) return;
      const isSpace = /^\s+$/.test(word);
      const w = measure(word, run.bold, run.italics);
      if (x + w > maxWidth && x > 0) {
        lines += 1;
        x = 0;
        if (isSpace) return;
      }
      x += w;
    });
  });
  return lines;
};

/** The chat's question bubble, in print: gutter Q badge + rounded tinted card. */
const questionBubble = (runs) => {
  const boldRuns = runs.map((r) => ({ ...r, bold: true }));
  const plainQuestionText = runs.map((r) => r.text).join('');
  const cardWidth = CONTENT_WIDTH - BADGE_SIZE - BUBBLE_GAP;
  const textWidth = cardWidth - BUBBLE_PAD_X * 2;
  // A few points of safety margin on the estimate's own wrap width, so a
  // font-metrics mismatch between the browser's canvas and pdfmake's
  // embedded font biases toward an extra line rather than a cramped one.
  const lines = wrappedLineCount(boldRuns, BUBBLE_FONT_SIZE, textWidth - 6);
  const lineHeight = BUBBLE_FONT_SIZE * 1.35;
  const cardHeight = lines * lineHeight + BUBBLE_PAD_Y * 2;

  return {
    // Never split a question bubble across a page break — it's meant to read
    // as one small, self-contained card, not a fragment of one.
    unbreakable: true,
    columns: [
      {
        width: BADGE_SIZE,
        stack: [
          { canvas: [{ type: 'ellipse', x: BADGE_SIZE / 2, y: BADGE_SIZE / 2, r1: BADGE_SIZE / 2, r2: BADGE_SIZE / 2, color: pal.accent }] },
          { text: 'Q', color: pal.onAccent, bold: true, fontSize: 9, alignment: 'center', relativePosition: { x: 0, y: -13.5 } },
        ],
      },
      {
        width: cardWidth,
        stack: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: cardWidth, h: cardHeight, r: BUBBLE_RADIUS, color: pal.accentSoft }] },
          // The horizontal padding is a spacer column plus a fixed text width,
          // not a margin: pdfmake ignores `margin` on a node positioned with
          // `relativePosition`, so the text sat flush against the card's left
          // edge and wrapped across its full width — one line wider than the
          // height estimate above allowed for.
          {
            columns: [
              { width: BUBBLE_PAD_X, text: '' },
              {
                width: textWidth,
                text: boldRuns,
                fontSize: BUBBLE_FONT_SIZE,
                outline: true,
                outlineText: plainQuestionText,
                // A session's contents list the questions asked.
                tocItem: true,
              },
            ],
            columnGap: 0,
            relativePosition: { x: 0, y: -(cardHeight - BUBBLE_PAD_Y) },
          },
        ],
      },
    ],
    columnGap: BUBBLE_GAP,
    margin: [0, 0, 0, 14],
  };
};

/** A cell's visible text, wrap hints removed. */
const cellText = (cell) => [].concat(cell.text ?? '').map((r) => (typeof r === 'string' ? r : r.text ?? '')).join('').replace(ZERO_WIDTH_RE, '').trim();

// A count, money, percentage or rank, optionally with a short note in
// brackets: "105", "$170.0", "70%", "#1", "150 (100%)". A column made only of
// these is a number column — right-aligned and only as wide as its numbers.
// A table of at most this many rows (header included) is never split across pages.
const SHORT_TABLE_ROWS = 9;
// Longest cell, in characters, a text column can have and still size to fit.
const SHORT_LABEL = 24;
const NUMERIC_CELL_RE = /^[#~≈<>+\-−]?\s*[$€£₹]?\s*\d[\d,.]*\s*(?:%|[kKmMbB]n?)?(?:\s*\([^)]*\))?$/;

/**
 * A markdown/HTML table -> pdfmake's table content. Spans the full text width:
 * text columns share it, number columns take only what their numbers need.
 * Rules only, no fills — a small muted header over an ink rule, hairlines
 * between rows — so a table reads the same on any theme's paper.
 */
const tableContent = (rows) => {
  if (!rows.length) return null;
  // A table of distinct headline figures prints as KPI tiles instead (see kpiTiles).
  const tiles = kpiTiles(rows);
  if (tiles) return tiles;
  const cols = Math.max(...rows.map((r) => r.length));
  const p = pal;
  const numeric = Array.from({ length: cols }, (_, ci) => rows.length > 1
    && rows.slice(1).every((r) => r[ci] && NUMERIC_CELL_RE.test(cellText(r[ci]))));
  // A text column of short labels ("Volume", "Initiative") sizes to them, so
  // the prose column beside it gets the width. With nothing left to stretch,
  // the first text column does, so the table still spans the page.
  const widths = numeric.map((n, ci) => (n || Math.max(...rows.map((r) => (r[ci] ? cellText(r[ci]).length : 0))) <= SHORT_LABEL ? 'auto' : '*'));
  if (!widths.includes('*')) widths[Math.max(numeric.indexOf(false), 0)] = '*';
  const table = {
    table: {
      headerRows: 1,
      // A row taller than the space left on a page otherwise gets split mid-
      // cell — a "(dataset-handle)" continuation line stranded at the top of
      // the next page with none of its own row's other cells beside it, no
      // border tying it back. Keeping each row whole moves the split point
      // to a row boundary instead.
      dontBreakRows: true,
      widths,
      body: rows.map((row, ri) => {
        const cells = row.slice(0, cols);
        while (cells.length < cols) cells.push({ text: '' });
        return cells.map((cell, ci) => ({
          ...cell,
          bold: ri === 0 || cell.bold,
          ...(ri === 0 ? {
            text: [].concat(cell.text ?? '').map((r) => (typeof r === 'string' ? r.toUpperCase() : { ...r, text: String(r.text).toUpperCase() })),
            fontSize: 7,
            characterSpacing: 0.6,
            color: p.muted,
          } : {}),
          alignment: cell.alignment || (numeric[ci] ? 'right' : undefined),
        }));
      }),
    },
    layout: {
      hLineWidth: (i) => (i === 0 ? 0 : (i === 1 ? 1 : 0.5)),
      hLineColor: (i) => (i === 1 ? p.ink : p.rule),
      vLineWidth: () => 0,
      paddingLeft: (i) => (i === 0 ? 0 : 8),
      paddingRight: (i, node) => (i === node.table.widths.length - 1 ? 0 : 8),
      paddingTop: () => 6,
      paddingBottom: () => 6,
    },
    margin: [0, 6, 0, 18],
    // A short table moves whole rather than leave one row on the next page
    // under a repeated header; a long one still splits at row boundaries.
    ...(rows.length <= SHORT_TABLE_ROWS ? { unbreakable: true } : {}),
  };
  // A table whose shape a chart can show (see chartFor) gets one above it;
  // the table stays, so every exact figure is still on the page.
  const chart = chartFor(rows, numeric);
  if (!chart) return table;
  return { stack: [chart, table], ...(rows.length <= SHORT_TABLE_ROWS ? { unbreakable: true } : {}) };
};

// ---- KPI tiles --------------------------------------------------------------
//
// "Metric | Value" tables — 150 tickets, 32.5% share, rank #1 — are a set of
// separate headline figures, not one series to compare, so a bar chart would
// misread them. They print as tiles: label, the figure large, its note. The
// tiles carry every word of the table, so they replace it.

const KPI_MIN_ROWS = 2;
const KPI_MAX_ROWS = 8;
const KPI_NOTE_MAX = 48;
const KPI_PER_ROW = 4;
const KPI_HEADER_RE = /\b(metric|kpi|measure|indicator|figure|value|result)s?\b/i;
// A figure at the start of a cell, and whatever follows it.
const KPI_VALUE_RE = /^([#~≈<>+\-−]?\s*[$€£₹]?\s*\d[\d,.]*\s*(?:%|[kKmMbB]n?|x)?)\s*(.*)$/;

/** "$1.2M" -> "$", "70%" -> "%", "#1" -> "#", "150" -> "" — what a figure counts in. */
const unitOf = (figure) => `${figure.match(/^[#$€£₹]/)?.[0] || ''}${figure.match(/(%|[kKmMbB]n?|x)$/)?.[0] || ''}`;

/**
 * Tiles for a two-column table whose every value starts with a figure, or
 * null. Separate figures, as opposed to one measure across entities (that is
 * a bar chart), show in any of three ways: the header names them ("Metric",
 * "KPI", "Value"), their units differ, or they carry notes. A note longer
 * than a tile can hold means the table is prose with numbers in it — no tiles.
 */
const kpiTiles = (rows) => {
  const body = rows.slice(1);
  if (rows[0].length !== 2 || body.length < KPI_MIN_ROWS || body.length > KPI_MAX_ROWS) return null;
  const items = body.map((r) => {
    const label = r[0] ? cellText(r[0]) : '';
    const m = (r[1] ? cellText(r[1]) : '').match(KPI_VALUE_RE);
    if (!label || NUMERIC_CELL_RE.test(label) || !m) return null;
    // "— nearly 1 in 3" / "(next: Vinod, 88)" -> "nearly 1 in 3" / "next: Vinod, 88"
    const note = m[2].replace(/^[\s—–\-:,·]+/, '').replace(/^\((.*)\)$/, '$1').trim();
    return { label, figure: m[1].replace(/\s+/g, ''), note };
  });
  if (items.some((it) => !it || it.note.length > KPI_NOTE_MAX)) return null;
  const named = KPI_HEADER_RE.test(cellText(rows[0][0])) || KPI_HEADER_RE.test(cellText(rows[0][1]));
  const mixedUnits = new Set(items.map((it) => unitOf(it.figure))).size > 1;
  const noted = items.some((it) => it.note);
  if (!named && !mixedUnits && !noted) return null;

  // Figure first, so figures line up across a row whatever their labels wrap to.
  const tile = (it) => ({
    stack: [
      { text: it.figure, fontSize: 18, bold: true, color: pal.ink },
      { text: it.label, fontSize: 8, color: pal.inkSoft, margin: [0, 3, 0, 0] },
      ...(it.note ? [{ text: it.note, fontSize: 7.5, color: pal.muted, margin: [0, 2, 0, 0] }] : []),
    ],
    fillColor: pal.surface,
  });
  // Rows as even as the count allows: 5 -> 3 + 2, 6 -> 3 + 3, 8 -> 4 + 4.
  const perRow = Math.ceil(items.length / Math.ceil(items.length / KPI_PER_ROW));
  const tileRows = [];
  for (let i = 0; i < items.length; i += perRow) {
    const cells = items.slice(i, i + perRow).map(tile);
    while (cells.length < perRow) cells.push({ text: '' });
    tileRows.push(cells);
  }
  // One table, so every tile in a row takes the tallest one's height; the
  // gaps between tiles are paper-coloured rules.
  const p = pal;
  const GAP = 8;
  return {
    table: { widths: new Array(perRow).fill('*'), body: tileRows, dontBreakRows: true },
    layout: {
      vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 0 : GAP),
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0 : GAP),
      vLineColor: () => p.paper,
      hLineColor: () => p.paper,
      paddingLeft: () => 10,
      paddingRight: () => 10,
      paddingTop: () => 9,
      paddingBottom: () => 9,
    },
    unbreakable: true,
    margin: [0, 4, 0, 12],
  };
};

// ---- Charts -----------------------------------------------------------------
//
// An answer arrives as markdown, with its numbers in tables. A table of a shape
// a chart reads at a glance gets one, drawn in pdfmake's own vector canvas in
// the theme's colours. The rules are deliberately narrow: a table that fits
// none stays a table, so a wrong guess costs a chart, never a misleading one.
//
//   share column summing to ~100%   -> one stacked bar + legend (composition)
//   Total + Done/Completed columns  -> a progress bar per row
//   one number column, time labels  -> columns in label order (change over time)
//   one number column, other labels -> horizontal bars, largest first (ranking)

const CHART_MIN_ROWS = 2;
const CHART_MAX_ROWS = 12;
const CHART_LABEL_WIDTH = 120;
const CHART_VALUE_WIDTH = 64;
const CHART_GAP = 8;
const CHART_BAR_WIDTH = CONTENT_WIDTH - CHART_LABEL_WIDTH - CHART_VALUE_WIDTH - CHART_GAP * 2;
const CHART_BAR_HEIGHT = 8;
const CHART_RADIUS = 2;
const COLUMN_CHART_HEIGHT = 84;
const DONE_HEADER_RE = /\b(done|completed?|closed|resolved|fixed)\b/i;
const TIME_LABEL_RE = /^(sprint|iteration|week|wk|q[1-4]\b|h[12]\b|fy\s?\d|(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b|\d{4}([-/]\d{1,2})?\b|\d{1,2}[-/]\d{1,2}\b)/i;

/** The number a cell states: "1,051" -> 1051, "70%" -> 70, "150 (100%)" -> 150. */
const numberOf = (text) => {
  const m = text.replace(/\([^)]*\)/g, '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : NaN;
};

const label8 = (text) => ({ text, fontSize: 8.5, color: pal.inkSoft });
const value8 = (text) => ({ text, fontSize: 8.5, color: pal.ink, bold: true, alignment: 'right' });
const roundedBar = (w, h, color, x = 0, y = 0) => ({ type: 'rect', x, y, w: Math.max(w, 0), h, r: Math.min(CHART_RADIUS, w / 2), color });

/** label | bar | value — the row both bar charts are made of. */
const barRow = (label, canvas, value) => ({
  columns: [
    { width: CHART_LABEL_WIDTH, ...label8(label) },
    { width: CHART_BAR_WIDTH, canvas, margin: [0, 2.5, 0, 0] },
    { width: CHART_VALUE_WIDTH, ...value8(value) },
  ],
  columnGap: CHART_GAP,
  margin: [0, 0, 0, 5],
});

/**
 * Every chart's key: a swatch per series, its name, and (for a composition)
 * its value — so no series is told apart by colour alone. Text wears the ink
 * tokens, never the series colour; four items to a row.
 */
const LEGEND_PER_ROW = 4;
const chartLegend = (items) => {
  const cells = items.map((it) => ({
    width: '*',
    columns: [
      {
        width: 7,
        // A swatch as faint as its track (the progress chart's "Total") gets
        // an outline, or it would vanish into the paper.
        canvas: [{ ...roundedBar(7, 7, it.color, 0, 2), ...(it.outline ? { lineColor: pal.muted, lineWidth: 0.5 } : {}) }],
      },
      {
        width: '*',
        text: [{ text: it.label, color: pal.inkSoft }, ...(it.value ? [{ text: `  ${it.value}`, bold: true, color: pal.ink }] : [])],
        fontSize: 8.5,
      },
    ],
    columnGap: 5,
  }));
  const rows = [];
  // Fixed equal slots, padded to a full row: pdfmake measures 'auto' columns
  // short here, which wrapped "QA Testing 11%" onto two lines.
  for (let i = 0; i < cells.length; i += LEGEND_PER_ROW) {
    const row = cells.slice(i, i + LEGEND_PER_ROW);
    while (row.length < LEGEND_PER_ROW) row.push({ width: '*', text: '' });
    rows.push({ columns: row, columnGap: 12, margin: [0, 0, 0, 3] });
  }
  return { stack: rows, margin: [0, 6, 0, 0] };
};

/** One stacked bar across the text width, a 2pt paper gap between segments, legend below. */
const compositionChart = (labels, values) => {
  const total = values.reduce((a, b) => a + b, 0);
  const gaps = (values.length - 1) * 2;
  let x = 0;
  const segments = values.map((v, i) => {
    const w = ((CONTENT_WIDTH - gaps) * v) / total;
    const seg = roundedBar(w, 12, pal.series[i]);
    seg.x = x;
    x += w + 2;
    return seg;
  });
  return {
    stack: [{ canvas: segments }, chartLegend(labels.map((l, i) => ({ label: l, value: `${values[i]}%`, color: pal.series[i] })))],
    margin: [0, 4, 0, 12],
  };
};

/** A track per row, filled to done/total, "52 / 73" at the end; the legend names both. */
const progressChart = (labels, done, total, names) => ({
  stack: [
    ...labels.map((l, i) => barRow(l, [
      roundedBar(CHART_BAR_WIDTH, CHART_BAR_HEIGHT, pal.sunken),
      roundedBar((CHART_BAR_WIDTH * Math.min(done[i], total[i])) / total[i], CHART_BAR_HEIGHT, pal.accent),
    ], `${done[i]} / ${total[i]}`)),
    chartLegend([{ label: names.done, color: pal.accent }, { label: names.total, color: pal.sunken, outline: true }]),
  ],
  margin: [0, 4, 0, 10],
});

/** Horizontal bars from zero, largest first; the legend names the measure. */
const rankingChart = (labels, values, shown, name) => {
  const max = Math.max(...values);
  const order = values.map((v, i) => i).sort((a, b) => values[b] - values[a]);
  return {
    stack: [
      ...order.map((i) => barRow(labels[i], [roundedBar((CHART_BAR_WIDTH * values[i]) / max, CHART_BAR_HEIGHT, pal.accent)], shown[i])),
      chartLegend([{ label: name, color: pal.accent }]),
    ],
    margin: [0, 4, 0, 10],
  };
};

/** Columns from a shared baseline, in label order, value under each label; the legend names the measure. */
const timeChart = (labels, values, shown, name) => {
  const max = Math.max(...values);
  const colWidth = CONTENT_WIDTH / labels.length;
  const barWidth = Math.min(28, colWidth * 0.6);
  const columns = {
    columns: labels.map((l, i) => {
      const h = (COLUMN_CHART_HEIGHT * values[i]) / max;
      return {
        width: colWidth,
        stack: [
          { canvas: [roundedBar(barWidth, h, pal.accent, (colWidth - barWidth) / 2, COLUMN_CHART_HEIGHT - h)] },
          { text: l, fontSize: 7.5, color: pal.inkSoft, alignment: 'center', margin: [0, 4, 0, 0] },
          { text: shown[i], fontSize: 7.5, bold: true, color: pal.ink, alignment: 'center' },
        ],
      };
    }),
    columnGap: 0,
  };
  return { stack: [columns, chartLegend([{ label: name, color: pal.accent }])], margin: [0, 4, 0, 12] };
};

/**
 * The chart a table's shape calls for, or null. Needs a text label column
 * first, 2-12 data rows, and non-negative numbers — bars start at zero, and a
 * longer table is a list to read, not a picture to glance at.
 */
const chartFor = (rows, numeric) => {
  const body = rows.slice(1);
  if (body.length < CHART_MIN_ROWS || body.length > CHART_MAX_ROWS || numeric[0]) return null;
  const labels = body.map((r) => cellText(r[0]));
  const headers = rows[0].map((c) => cellText(c));
  const col = (ci) => body.map((r) => numberOf(cellText(r[ci])));
  const numCols = numeric.map((n, ci) => (n ? ci : -1)).filter((ci) => ci > 0);
  if (!numCols.length || numCols.some((ci) => col(ci).some((v) => Number.isNaN(v) || v < 0))) return null;

  const share = numCols.find((ci) => /%/.test(headers[ci]) || body.every((r) => /%\s*$/.test(cellText(r[ci]))));
  if (share !== undefined && body.length <= pal.series.length) {
    const sum = col(share).reduce((a, b) => a + b, 0);
    if (sum >= 97 && sum <= 103) return compositionChart(labels, col(share));
  }

  const totalCol = numCols.find((ci) => /\btotal\b/i.test(headers[ci]));
  const doneCol = numCols.find((ci) => DONE_HEADER_RE.test(headers[ci]));
  if (totalCol !== undefined && doneCol !== undefined && col(totalCol).every((t) => t > 0)) {
    return progressChart(labels, col(doneCol), col(totalCol), { done: headers[doneCol], total: headers[totalCol] });
  }

  if (numCols.length !== 1) return null;
  const values = col(numCols[0]);
  if (Math.max(...values) <= 0) return null;
  const shown = body.map((r) => cellText(r[numCols[0]]));
  const name = headers[numCols[0]] || 'Value';
  return labels.every((l) => TIME_LABEL_RE.test(l)) ? timeChart(labels, values, shown, name) : rankingChart(labels, values, shown, name);
};

// Question bubbles are never derived from a heading depth encountered while
// parsing prose — see sessionSegments' doc comment for why. Every heading
// found while walking mdast (including a "## " an agent wrote to structure
// its own answer) is just a heading, at a size stepping down from the title.
const HEADING_STYLE = { 1: 15, 2: 13, 3: 11.5, 4: 10.5, 5: 10, 6: 10 };
// Extra top space before each level, on top of what naturally separates
// blocks — a bigger gap before a bigger heading reads as "new section"
// rather than every heading looking like the same kind of break.
const HEADING_MARGIN_TOP = { 1: 24, 2: 18, 3: 12 };

/**
 * A heading at any depth (mdast or hast, markdown or HTML branch) -> the same
 * pdfmake content, so a "Key Insights" HTML card's own headings read at the
 * same visual weight as the surrounding markdown's. Depth 1 gets a rule under
 * it — a bigger gap alone reads as "some space", a rule reads as "new
 * section" — while flipping through a long multi-page document.
 */
// Which heading depth lists in the cover's contents — set per build, from how
// the answer is actually structured (see contentsDepth). 0 = none.
let tocDepth = 0;

const headingBlock = (runs, depth) => {
  const size = HEADING_STYLE[depth] || 11;
  const marginTop = HEADING_MARGIN_TOP[depth] || 10;
  // "1. Overall Standing" prints as an accent "01" ahead of the words — the
  // section number reads as structure, not as part of the title.
  const [first, ...rest] = runs;
  const numbered = first && String(first.text).match(/^\s*(\d{1,2})[.)]\s+/);
  const titleRuns = numbered ? [{ ...first, text: first.text.slice(numbered[0].length) }, ...rest] : runs;
  const boldRuns = [
    ...(numbered ? [{ text: `${numbered[1].padStart(2, '0')}   `, color: pal.accentInk, bold: true }] : []),
    ...titleRuns.map((r) => ({ ...r, bold: true })),
  ];
  // Depth 1/2 headings become PDF bookmarks (the reader's own viewer sidebar)
  // — real navigation for a multi-page document, absent until now. Depth 3+
  // stays out: that granular would turn the bookmark panel into noise.
  const outlineProps = depth <= 2 ? { outline: true, outlineText: titleRuns.map((r) => r.text).join('').trim() } : {};
  const tocProps = depth === tocDepth ? { tocItem: true } : {};
  // Read by keepHeadingWithContent, so a heading never ends a page alone.
  const keepProps = { headlineLevel: depth };
  if (depth === 1) {
    return {
      stack: [
        { text: boldRuns, fontSize: size, margin: [0, 0, 0, 6], ...outlineProps, ...tocProps, ...keepProps },
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: pal.accent, lineWidth: 1.5 }] },
      ],
      margin: [0, marginTop, 0, 12],
    };
  }
  return { text: boldRuns, fontSize: size, margin: [0, marginTop, 0, depth === 2 ? 8 : 4], ...outlineProps, ...tocProps, ...keepProps };
};

/** mdast block nodes -> an array of pdfmake content nodes. */
const mdastBlock = (node) => {
  switch (node.type) {
    case 'heading':
      return [headingBlock(mdastInline(node.children), node.depth)];
    case 'paragraph': {
      const runs = mdastInline(node.children);
      if (!runs.length) return [];
      // A paragraph that is *entirely* `_italic_` is one of this file's own
      // meta notes ("No answer yet.", "This chat had no messages.") — muted,
      // like the app's own meta text, not full-ink prose.
      if (node.children.length === 1 && node.children[0].type === 'emphasis') {
        return [{ text: runs, italics: true, color: pal.muted, fontSize: 9.5, margin: [0, 0, 0, 12] }];
      }
      return [{ text: runs, margin: [0, 0, 0, 12] }];
    }
    case 'list':
      return [{
        [node.ordered ? 'ol' : 'ul']: node.children.map(mdastListItem),
        markerColor: pal.accent,
        margin: [0, 0, 0, 9],
      }];
    case 'table':
      return [tableContent(node.children.map(
        (row) => row.children.map((cell, colIdx) => ({ text: mdastInline(cell.children), alignment: mdastAlign(node, colIdx) })),
      ))].filter(Boolean);
    case 'code':
      return [panel(
        // Emoji go here too (Courier has no glyphs either); no wrap hints —
        // a code block keeps its own line breaks.
        { text: stripEmoji(node.value), font: 'Courier', fontSize: 9, color: pal.ink },
        { fillColor: pal.surface, margin: [0, 4, 0, 14], contentMargin: [8, 8, 8, 8] },
      )];
    case 'thematicBreak':
      return [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: pal.rule }], margin: [0, 8, 0, 14] }];
    case 'blockquote':
      return [{ stack: node.children.flatMap(mdastBlock), margin: [0, 2, 0, 8], color: pal.inkSoft }];
    case 'html':
      return htmlBlock(parseHtmlFragment(node.value));
    default:
      return node.children ? node.children.flatMap(mdastBlock) : [];
  }
};

const mdastAlign = (tableNode, colIdx) => {
  const align = tableNode.align?.[colIdx];
  return align === 'center' || align === 'right' ? align : undefined;
};

/** One `listItem`'s children (a paragraph, maybe a nested list) -> one pdfmake list entry. */
const mdastListItem = (item) => {
  const parts = item.children.flatMap((child) => (
    child.type === 'paragraph' ? [{ text: mdastInline(child.children) }] : mdastBlock(child)
  ));
  if (!parts.length) return { text: '' };
  return parts.length === 1 ? parts[0] : { stack: parts };
};

/** Parse a fragment of raw HTML (already sanitized) into a hast node list. */
const parseHtmlFragment = (html) => {
  const tree = unified().use(rehypeParse, { fragment: true }).parse(sanitizeHTML(html));
  return tree.children || [];
};

const isBlockTag = (node) => node.type === 'element'
  && ['p', 'div', 'section', 'ul', 'ol', 'table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'hr'].includes(node.tagName);

/** hast block nodes (parsed HTML, e.g. a "Key Insights" card) -> pdfmake content nodes. */
const htmlBlock = (nodes = []) => nodes.flatMap((node) => {
  if (node.type === 'text') return node.value.trim() ? [{ text: breakable(node.value), margin: [0, 0, 0, 8] }] : [];
  if (node.type !== 'element') return [];
  const kids = () => node.children || [];
  switch (node.tagName) {
    case 'p':
      return [{ text: hastInline(kids()), margin: [0, 0, 0, 8] }];
    case 'h1': return [headingBlock(hastInline(kids()), 1)];
    case 'h2': return [headingBlock(hastInline(kids()), 2)];
    case 'h3': case 'h4': case 'h5': case 'h6':
      return [headingBlock(hastInline(kids()), Number(node.tagName[1]))];
    case 'ul':
      return [{ ul: kids().filter((c) => c.tagName === 'li').map(htmlListItem), markerColor: pal.accent, margin: [0, 0, 0, 9] }];
    case 'ol':
      return [{ ol: kids().filter((c) => c.tagName === 'li').map(htmlListItem), markerColor: pal.accent, margin: [0, 0, 0, 9] }];
    case 'table':
      return [tableContent(htmlTableRows(node))].filter(Boolean);
    case 'hr':
      return [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: pal.rule }], margin: [0, 8, 0, 14] }];
    case 'blockquote':
      return [{ stack: htmlBlock(kids()), margin: [0, 2, 0, 8], color: pal.inkSoft }];
    // Structural wrappers with no own presentation — descend into their content.
    case 'div': case 'section': case 'span': case 'body': case 'html':
      return kids().some(isBlockTag) ? htmlBlock(kids()) : [{ text: hastInline(kids()), margin: [0, 0, 0, 8] }];
    default:
      return htmlBlock(kids());
  }
});

const htmlListItem = (li) => {
  const parts = htmlBlock(li.children || []);
  if (!parts.length) return { text: '' };
  return parts.length === 1 ? parts[0] : { stack: parts };
};

const htmlTableRows = (tableNode) => {
  const rows = [];
  const collect = (node) => (node.children || []).forEach((c) => {
    if (c.tagName === 'tr') rows.push(c);
    else if (['thead', 'tbody', 'tfoot'].includes(c.tagName)) collect(c);
  });
  collect(tableNode);
  return rows.map((tr) => (tr.children || [])
    .filter((c) => c.tagName === 'td' || c.tagName === 'th')
    .map((cell) => ({ text: hastInline(cell.children), bold: cell.tagName === 'th' })));
};

/**
 * The brand an export is stamped with: the same white-label values the app's
 * top bar shows (REACT_APP_BRAND_NAME / _TAGLINE / _LOGO_URL, via runtime
 * config), so a client deployment's documents carry the client's mark. The
 * builders take the brand as a plain argument and stay synchronous; the
 * download helpers resolve it — including turning the logo URL into a data
 * URL, which is the only form pdfmake can embed — before building.
 */
export const DEFAULT_BRAND = { name: 'Discover', tagline: 'by elastiq.ai' };

const brandFromConfig = () => ({
  name: config.brandName,
  tagline: config.brandTagline,
  logoUrl: config.brandLogoUrl,
});

const brandOrDefault = (brand = {}) => ({
  name: (brand.name && String(brand.name).trim()) || DEFAULT_BRAND.name,
  tagline: brand.tagline == null ? DEFAULT_BRAND.tagline : String(brand.tagline).trim(),
  logoDataUrl: brand.logoDataUrl || null,
});

/**
 * A logo as a data URL, or null. Only PNG and JPEG are worth fetching — pdfmake
 * embeds nothing else — and any failure (offline, 404, a non-image response, a
 * blocked cross-origin read) leaves the band with the wordmark alone rather
 * than failing the export. A successful fetch is cached per URL — one fetch
 * per session, not one per click — while a null is never cached, so a logo
 * that was briefly unreachable comes back on the next export.
 */
const logoDataUrlCache = new Map();
export const fetchImageDataUrl = async (url) => {
  if (!url || typeof fetch !== 'function') return null;
  if (!logoDataUrlCache.has(url)) {
    logoDataUrlCache.set(url, (async () => {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!/^image\/(png|jpe?g)$/i.test(blob.type)) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    })().catch(() => null).then((dataUrl) => {
      if (!dataUrl) logoDataUrlCache.delete(url); // only a real logo is worth keeping
      return dataUrl;
    }));
  }
  return logoDataUrlCache.get(url);
};

/** The configured brand with its logo fetched, ready for a builder. */
export const resolveBrand = async (brand = brandFromConfig()) => ({
  ...brandOrDefault(brand),
  logoDataUrl: brand.logoDataUrl || await fetchImageDataUrl(brand.logoUrl),
});

const COVER_WIDTH = A4_WIDTH - COVER_MARGINS[0] - COVER_MARGINS[2];

/** A small spaced-capitals label: "ASKED", "CONTENTS". */
const label = (text, color = pal.muted) => ({ text: text.toUpperCase(), fontSize: 7.5, bold: true, characterSpacing: 1.2, color });

/** The deployment's mark: its logo when one resolved, beside the name and tagline. */
const brandMark = (b) => {
  const wordmark = {
    text: [{ text: b.name, bold: true }, ...(b.tagline ? [{ text: `  ${b.tagline}`, color: pal.muted }] : [])],
    fontSize: 9.5,
  };
  return b.logoDataUrl
    ? { columns: [{ image: b.logoDataUrl, fit: [96, 22], width: 'auto' }, { ...wordmark, margin: [0, 6, 0, 0] }], columnGap: 10 }
    : wordmark;
};

/** "Asked …" / "Exported by … · …" -> a cover label and its value. */
const coverMeta = (line) => {
  const m = line.match(/^(Asked|Exported by|Exported ·)\s+(.+)$/);
  return m ? { label: m[1].replace(' ·', ''), value: m[2] } : { label: '', value: line };
};

/** The leading "_Asked …_" / "_Exported …_" stamps, which the cover shows instead of the body. */
const isStampParagraph = (node) => node?.type === 'paragraph'
  && node.children.length === 1
  && node.children[0].type === 'emphasis'
  && /^(Asked|Exported)\b/.test(plainText(node));

/**
 * The heading depth the cover's contents list: whichever depth the answer
 * uses most (at least twice), so "## Profile" over eight "### 1. …" sections
 * lists the eight. A depth with more than 14 headings would push the contents
 * off the cover, so it is passed over.
 */
const contentsDepth = (nodes) => {
  const counts = {};
  nodes.forEach((n) => { if (n.type === 'heading' && n.depth <= 3) counts[n.depth] = (counts[n.depth] || 0) + 1; });
  return [1, 2, 3]
    .filter((d) => counts[d] >= 2 && counts[d] <= 14)
    .reduce((best, d) => (!best || counts[d] > counts[best] ? d : best), 0);
};

/**
 * Agents put "---" between every section, so each heading printed with a rule
 * above it as well as its own spacing (and, at depth 1, its own underline).
 * A rule next to a heading — or at either end — goes; one between two
 * paragraphs still marks a break.
 */
const dropRulesBesideHeadings = (nodes) => nodes.filter((n, i) => n.type !== 'thematicBreak'
  || (i > 0 && i < nodes.length - 1 && nodes[i - 1].type !== 'heading' && nodes[i + 1].type !== 'heading'));

/**
 * The rail's title: the first few words, enough to say what the document is.
 * Emoji stripped here — a caller's raw title (no "# " heading in the
 * markdown, or a session's own title) reaches this without passing through
 * the inline-run path that strips them everywhere else.
 */
const shortTitle = (text, words = 8) => {
  const w = stripEmoji(text).trim().split(/\s+/);
  return w.length > words ? `${w.slice(0, words).join(' ')}…` : w.join(' ');
};

const pageFill = (size, p) => ({ canvas: [{ type: 'rect', x: 0, y: 0, w: size.width, h: size.height, color: p.paper }] });

/**
 * Page 1: the brand, the question large, then who and when beside the
 * contents. Its own section, so it keeps even margins and no rail.
 */
const coverSection = ({ eyebrow, title, meta, brand, showContents }) => {
  const p = pal;
  const metaStack = meta.flatMap((m) => [
    ...(m.label ? [label(m.label)] : []),
    { text: m.value, fontSize: 10, color: p.ink, margin: [0, 3, 0, 12] },
  ]);
  return {
    section: [
      brandMark(brand),
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: COVER_WIDTH, y2: 0, lineWidth: 0.75, lineColor: p.rule }], margin: [0, 12, 0, 0] },
      { ...label(eyebrow, p.accentInk), margin: [0, 96, 0, 10] },
      { text: withMentionChips([{ text: title }]), fontSize: 22, bold: true, lineHeight: 1.22, color: p.ink },
      { canvas: [{ type: 'rect', x: 0, y: 0, w: 48, h: 3, r: 1.5, color: p.accent }], margin: [0, 22, 0, 0] },
      {
        columns: [
          { width: '*', stack: metaStack },
          ...(showContents ? [{
            width: '*',
            stack: [
              label('Contents'),
              { toc: { textStyle: { fontSize: 8.5, color: p.inkSoft }, numberStyle: { fontSize: 8.5, color: p.muted }, textMargin: [0, 1, 0, 1] }, margin: [0, 6, 0, 0] },
            ],
          }] : []),
        ],
        columnGap: 32,
        margin: [0, 48, 0, 0],
      },
    ],
    pageMargins: COVER_MARGINS,
    background: (currentPage, size) => pageFill(size, p),
    footer: null,
  };
};

/**
 * Pages 2+: the rail down the left edge — brand, what the document is — drawn
 * as each page's background, so it repeats on every page the body flows onto.
 * Background and footer run at layout time, after this build returns, so they
 * close over this document's palette rather than reading the module's.
 */
const bodySection = ({ content, brand, railLabel, railTitle, askedOn }) => {
  const p = pal;
  const railText = RAIL_WIDTH - RAIL_PAD * 2;
  return {
    section: content,
    pageMargins: BODY_MARGINS,
    background: (currentPage, size) => [
      {
        canvas: [
          { type: 'rect', x: 0, y: 0, w: size.width, h: size.height, color: p.paper },
          { type: 'rect', x: 0, y: 0, w: RAIL_WIDTH, h: size.height, color: p.surface },
          { type: 'line', x1: RAIL_WIDTH, y1: 0, x2: RAIL_WIDTH, y2: size.height, lineWidth: 0.75, lineColor: p.rule },
        ],
        absolutePosition: { x: 0, y: 0 },
      },
      {
        // A fixed-width column, or the rail's text would wrap at the page's width.
        columns: [{
          width: railText,
          stack: [
            ...(brand.logoDataUrl ? [{ image: brand.logoDataUrl, fit: [railText, 18], margin: [0, 0, 0, 6] }] : []),
            { text: brand.name, bold: true, fontSize: 9, color: p.ink },
            ...(brand.tagline ? [{ text: brand.tagline, fontSize: 7.5, color: p.muted }] : []),
            { ...label(railLabel, p.muted), fontSize: 6.5, margin: [0, 22, 0, 4] },
            { text: railTitle, fontSize: 8.5, lineHeight: 1.35, color: p.inkSoft },
            ...(askedOn ? [
              { ...label('Asked', p.muted), fontSize: 6.5, margin: [0, 16, 0, 4] },
              { text: askedOn, fontSize: 8.5, color: p.inkSoft },
            ] : []),
          ],
        }],
        absolutePosition: { x: RAIL_PAD, y: 44 },
      },
    ],
    // The page number sits at the foot of the rail, where the eye finds it.
    footer: (currentPage, pageCount) => ({
      margin: [RAIL_PAD, 8, 0, 0],
      columns: [{
        width: railText,
        stack: [
          { text: String(currentPage).padStart(2, '0'), fontSize: 18, bold: true, lineHeight: 1, color: p.accentInk },
          { text: `of ${String(pageCount).padStart(2, '0')}`, fontSize: 7.5, color: p.muted },
        ],
      }],
    }),
  };
};

/**
 * A heading that starts in the last quarter of a page (about 185pt — room for
 * a subtitle and a short table) moves to the next page, so "03 Work Type
 * Breakdown" never ends a page with its table on the following one. Measured
 * by position rather than by what follows: pdfmake calls this on its first
 * layout pass, before a short table is moved whole to the next page, and its
 * list of "following" nodes includes the rail's page number. The document's
 * last heading stays put — nothing follows it.
 */
const keepHeadingWithContent = (node, { getNodesOnNextPage }) => !!node.headlineLevel
  && node.startPosition?.verticalRatio > 0.75
  && getNodesOnNextPage().length > 0;

/** The page setup both exports share; every page's content lives in its section. */
const pdfDocument = ({ bodyFont, sections, info }) => ({
  pageSize: 'A4',
  pageMargins: BODY_MARGINS,
  defaultStyle: { font: bodyFont, fontSize: 10, color: pal.ink, lineHeight: 1.35 },
  content: sections,
  pageBreakBefore: keepHeadingWithContent,
  info,
});

/**
 * Compose the whole Markdown document into a pdfmake docDefinition — a cover
 * page, then the answer on railed pages. Parsing goes through remark
 * (the same parser react-markdown uses for the live chat bubble) plus
 * remark-gfm for tables, so nested/mixed lists and tables glued directly
 * under a heading parse exactly as they do on screen. A block of raw HTML
 * (e.g. an agent-formatted "Key Insights" card) is recognised by remark as
 * its own node and handed to a parallel HTML-to-content walker instead of
 * printing as literal tags.
 *
 * `theme` is a chat theme id (theme/tokens.js); the download helpers pass the
 * one the reader has active.
 *
 * Exported (rather than folded into `downloadPdf`) so its output — a plain
 * content tree — can be asserted on directly in tests, without a jsPDF/canvas
 * stand-in.
 */
export const buildPdfDocDefinition = (markdown, { title = 'Answer', bodyFont = 'Helvetica', brand, theme } = {}) => {
  activeBodyFont = bodyFont;
  pal = paletteFor(theme);
  const b = brandOrDefault(brand);
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown);
  let children = tree.children || [];

  let coverTitle = title || 'Answer';
  if (children[0]?.type === 'heading' && children[0].depth === 1) {
    coverTitle = plainText(children[0]) || coverTitle;
    children = children.slice(1);
  }
  const meta = [];
  while (isStampParagraph(children[0])) {
    meta.push(coverMeta(plainText(children[0])));
    children = children.slice(1);
  }

  tocDepth = contentsDepth(children);
  const content = dropRulesBesideHeadings(children).flatMap(mdastBlock);
  const asked = meta.find((m) => m.label === 'Asked');

  return pdfDocument({
    bodyFont,
    sections: [
      coverSection({ eyebrow: 'Question', title: coverTitle, meta, brand: b, showContents: tocDepth > 0 }),
      bodySection({ content, brand: b, railLabel: 'Answer', railTitle: shortTitle(coverTitle), askedOn: asked?.value.split(',')[0] }),
    ],
    info: { title: coverTitle, author: b.name, subject: `${b.name} answer export` },
  });
};

/**
 * Compose a whole session into a pdfmake docDefinition. Unlike
 * `buildPdfDocDefinition`, this never re-derives "is this a real question"
 * from a flat string's heading depth — it walks `sessionSegments`, so a
 * question bubble is used only for a segment this file itself tagged
 * `question` (an actual conversation turn), while every heading inside a
 * turn's own written answer — including one at the same "## " depth —
 * renders as a plain heading, via the same per-segment remark parse
 * `buildPdfDocDefinition` uses for the whole document. The cover's contents
 * list the questions asked.
 */
export const buildSessionPdfDocDefinition = (title, messages = [], { bodyFont = 'Helvetica', brand, exportedBy, theme } = {}) => {
  activeBodyFont = bodyFont;
  pal = paletteFor(theme);
  tocDepth = 0;
  const b = brandOrDefault(brand);
  const segments = sessionSegments(title, messages, { exportedBy });
  const coverTitle = segments[0].text;
  // segments[1] is always the "Exported …" stamp — the cover shows it.
  const stamp = plainText(unified().use(remarkParse).parse(segments[1].text));

  const content = [];
  let questions = 0;
  segments.slice(2).forEach((seg) => {
    if (seg.type === 'question') {
      questions += 1;
      content.push(questionBubble(withMentionChips(parseInlineOnly(seg.text))));
      return;
    }
    const tree = unified().use(remarkParse).use(remarkGfm).parse(seg.text);
    content.push(...dropRulesBesideHeadings(tree.children || []).flatMap(mdastBlock));
  });

  return pdfDocument({
    bodyFont,
    sections: [
      coverSection({ eyebrow: 'Conversation', title: coverTitle, meta: [coverMeta(stamp)], brand: b, showContents: questions >= 2 }),
      bodySection({ content, brand: b, railLabel: 'Conversation', railTitle: shortTitle(coverTitle) }),
    ],
    info: { title: coverTitle, author: b.name, subject: `${b.name} chat session export` },
  });
};

// Inter is the application's typeface; the PDF embeds it so the document
// looks like the product, not like a generic fallback. Fetched once from our
// own bundled assets and cached as base64 for every later export in the
// session.
const FONT_FILES = [
  { url: interRegularUrl, vfs: 'Inter-Regular.ttf', style: 'normal' },
  { url: interBoldUrl, vfs: 'Inter-Bold.ttf', style: 'bold' },
  { url: interItalicUrl, vfs: 'Inter-Italic.ttf', style: 'italic' },
];

let interFontsPromise = null;
const fetchInterFonts = () => {
  if (!interFontsPromise) {
    interFontsPromise = Promise.all(
      FONT_FILES.map(async (f) => {
        const res = await fetch(f.url);
        if (!res.ok) throw new Error(`font fetch failed: ${res.status}`);
        const buf = await res.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buf);
        const CHUNK = 0x8000; // avoid call-stack limits on fromCharCode
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
        }
        return { ...f, base64: btoa(binary) };
      }),
    ).catch((err) => {
      // Next export retries rather than being stuck with a rejected promise.
      interFontsPromise = null;
      throw err;
    });
  }
  return interFontsPromise;
};

/**
 * pdfmake (plus the standard-14 Helvetica/Courier font tables) is imported on
 * demand so its weight is paid on the first PDF click, never on page load —
 * the old print-dialog flow existed to avoid exactly that cost; code-splitting
 * removes the tradeoff. The app's own typeface is embedded too; a failed font
 * fetch degrades to the standard Helvetica rather than failing the export.
 */
const loadPdfMake = async () => {
  // The browser-bundle standard-font modules (not `pdfmake/standard-fonts/*`,
  // which assume Node's real filesystem for the AFM metrics) embed their own
  // AFM data as a virtual file system entry, via addFontContainer.
  const [{ default: pdfMake }, { default: helveticaFonts }, { default: courierFonts }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/standard-fonts/Helvetica'),
    import('pdfmake/build/standard-fonts/Courier'),
  ]);
  pdfMake.addFontContainer(helveticaFonts);
  pdfMake.addFontContainer(courierFonts);

  let bodyFont = 'Helvetica';
  try {
    const fonts = await fetchInterFonts();
    const vfs = {};
    const faces = {};
    fonts.forEach((f) => { vfs[f.vfs] = f.base64; faces[f.style] = f.vfs; });
    pdfMake.addVirtualFileSystem(vfs);
    pdfMake.addFonts({ Inter: { normal: faces.normal, bold: faces.bold, italics: faces.italic, bolditalics: faces.bold } });
    bodyFont = 'Inter';
  } catch {
    // Helvetica fallback already registered above.
  }
  return { pdfMake, bodyFont };
};

/** Compose and directly download the PDF — same gesture as the Markdown file. */
export const downloadPdf = async (markdown, title, filename, { brand } = {}) => {
  // The theme at the click, not at the end of the first export's font fetch.
  const theme = activeTheme();
  const [{ pdfMake, bodyFont }, resolvedBrand] = await Promise.all([loadPdfMake(), resolveBrand(brand)]);
  const docDefinition = buildPdfDocDefinition(markdown, { title, bodyFont, brand: resolvedBrand, theme });
  await pdfMake.createPdf(docDefinition).download(filename);
};

/** Compose and directly download the whole session as one PDF. */
export const downloadSessionPdf = async (title, messages, filename, { brand, exportedBy } = {}) => {
  const theme = activeTheme();
  const [{ pdfMake, bodyFont }, resolvedBrand] = await Promise.all([loadPdfMake(), resolveBrand(brand)]);
  const docDefinition = buildSessionPdfDocDefinition(title, messages, { bodyFont, brand: resolvedBrand, exportedBy, theme });
  await pdfMake.createPdf(docDefinition).download(filename);
};

/** Save `text` as a file. Returns false when the browser blocks it. */
export const downloadTextFile = (text, filename, mimeType = 'text/markdown;charset=utf-8') => {
  try {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on the next frame: revoking synchronously can cancel the download
    // in some browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch {
    return false;
  }
};
